import { useState, useEffect, useRef } from "react";
import { Bell, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function Notification({ notifications = [], onMarkAsRead = () => {}, onMarkAllAsRead }) {
    const [showNotifications, setShowNotifications] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef(null);

    // Xử lý click bên ngoài để đóng dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Format ngày giờ đầy đủ: dd/MM/yyyy HH:mm:ss
    const formatTime = (date) => {
        return new Intl.DateTimeFormat('vi-VN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false, // Sử dụng định dạng 24 giờ
        }).format(new Date(date));
    };

    // Format tiền VNĐ
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        }).format(amount);
    };
    

    // Lấy màu theo loại thông báo
    const getNotificationStyle = (type) => {
        switch (type) {
            case "success":
                return "bg-green-50 text-green-900 border-l-4 border-green-500";
            case "warning":
                return "bg-yellow-50 text-yellow-900 border-l-4 border-yellow-500";
            case "error":
                return "bg-red-50 text-red-900 border-l-4 border-red-500";
            default:
                return "bg-blue-50 text-blue-900 border-b border-blue-500";
        }
    };

    // Tách email và số tiền từ message
    const extractEmailAndAmount = (message) => {
        const emailMatch = message.match(/khách hàng ([\w.-]+@[\w.-]+\.\w+)/);
        const amountMatch = message.match(/số tiền là ([\d,.]+)/);
        const email = emailMatch ? emailMatch[1] : '';
        const amount = amountMatch ? parseFloat(amountMatch[1].replace(/,/g, '')) : 0;
        return { email, amount };
    };

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                disabled={isLoading}
            >
                <Bell className="w-6 h-6 text-gray-700" />
                {notifications.some((n) => !n.isRead) && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full animate-pulse">
            {notifications.filter((n) => !n.isRead).length}
          </span>
                )}
            </button>

            <AnimatePresence>
                {showNotifications && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-2 w-96 bg-white border shadow-xl rounded-lg overflow-hidden z-50"
                    >
                        <div className="p-3 border-b bg-gray-50 flex justify-between items-center">
                            <span className="font-semibold text-gray-700">Thông báo</span>
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                            {notifications.length === 0 ? (
                                <p className="p-4 text-gray-500 text-sm text-center">
                                    Không có thông báo nào
                                </p>
                            ) : (
                                notifications.map((notif) => {
                                    const { email, amount } = extractEmailAndAmount(notif.message);
                                    return (
                                        <div
                                            key={notif.id}
                                            className={`p-4 border-b text-sm cursor-pointer transition-colors duration-200 hover:bg-gray-50 ${notif.isRead ? "bg-gray-100 text-gray-600" : getNotificationStyle(notif.type)}`}
                                            onClick={async () => {
                                                if (!notif.isRead) {
                                                    setIsLoading(true);
                                                    try {
                                                        await onMarkAsRead(notif.id, notif.orderId);
                                                    } finally {
                                                        setIsLoading(false);
                                                    }
                                                }
                                                setShowNotifications(false);
                                            }}
                                        >
                                            <div className="space-y-1">
                                                <p className="font-semibold">{notif.title}</p>
                                                <div className="flex flex-col">
                          <span className="text-gray-800">
                            Đơn hàng từ khách hàng{" "}
                              <span className="font-medium text-blue-600">{email}</span>
                          </span>
                                                    <span className="text-gray-800">
                            được tạo mới với số tiền là{" "}
                                                        <span className="font-medium text-green-600">{formatCurrency(amount)}</span>
                          </span>
                                                </div>
                                                <span className="text-xs text-gray-500">
                          {formatTime(notif.createdAt)}
                        </span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}